(() => {
  const componentName = 'scroll-to-top';

  if (customElements.get(componentName)) return;

  const getScrollTop = () =>
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0;

  const setScrollTop = (value) => {
    const y = Math.max(0, value);
    const scrollingElement = document.scrollingElement || document.documentElement;
    scrollingElement.scrollTop = y;
    document.documentElement.scrollTop = y;
    document.body.scrollTop = y;
    window.scrollTo(0, y);
  };

  class ScrollToTop extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-scroll-to-top-button]');
      this.scrollThreshold = Math.max(Number(this.dataset.scrollThreshold) || 300, 0);
      this.ticking = false;
      this.animationFrame = null;
      this.isAnimating = false;

      this.handleScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.updateVisibility();
          this.ticking = false;
        });
      };

      this.handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.scrollPageToTop();
      };

      this.addEventListener('click', this.handleClick);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleScroll, { passive: true });
      this.updateVisibility();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleScroll);
      this.cancelScrollAnimation();
    }

    cancelScrollAnimation() {
      if (this.animationFrame !== null) {
        window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
      this.isAnimating = false;
    }

    scrollPageToTop() {
      const startY = getScrollTop();
      if (startY <= 0 || this.isAnimating) return;

      this.cancelScrollAnimation();
      this.isAnimating = true;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const startTime = performance.now();
      const duration = reduceMotion
        ? 350
        : Math.min(1400, Math.max(650, startY * 0.55));
      const easeOutCubic = (t) => 1 - (1 - t) ** 3;

      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        setScrollTop(startY * (1 - easeOutCubic(progress)));

        if (progress < 1) {
          this.animationFrame = window.requestAnimationFrame(step);
          return;
        }

        setScrollTop(0);
        this.animationFrame = null;
        this.isAnimating = false;
        this.updateVisibility();
      };

      this.animationFrame = window.requestAnimationFrame(step);
    }

    updateVisibility() {
      if (this.isAnimating) return;
      const shouldShow = getScrollTop() > this.scrollThreshold;
      this.classList.toggle('is-visible', shouldShow);
      if (this.button) {
        this.button.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        this.button.tabIndex = shouldShow ? 0 : -1;
      }
    }
  }

  customElements.define(componentName, ScrollToTop);
})();
